import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon } from "@nubeteck/components";

import { Input, Layout, Select, Table, Skeleton } from "antd";
import { StrapiError } from "../../StrapiError";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormInstance } from "antd/lib/form";
import { toastErrorStyle, useSuccessRegisterToast, useErrorFieldsNeedReviewToast } from "src/constants";
import {
  useCreateResolutionMutation,
  useGetResolutionsMutation,
  useUpdateResolutionMutation,
  useGetResolutionsStatusMutation,
} from "src/services";
import { ArrayUtils } from "@nubeteck/utils";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaResolutions";

interface IResolution {
  resolucionId: number;
  estadoId: number;
  resolucionNombre?: string;
  nombre?: string;
}

const Resolutions: React.FC = () => {
  const [form] = Form.useForm<IResolution>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [resolucionId, setResolucionId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const [translatedData, setTranslatedData] = React.useState<{
    resoluciones: { [key: string]: string };
    estados: { [key: string]: string };
  }>({
    resoluciones: {},
    estados: {},
  });
  const showErrorFieldsNeedReviewToast = useErrorFieldsNeedReviewToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const formRef = React.useRef<FormInstance>(null);
  const { resetFields, setFieldsValue, validateFields } = form;
  const [getResolutions, { data, isLoading }] = useGetResolutionsMutation();
  const [getResolutionsStatus, { data: statusList, isLoading: isLoadingStatus }] =
    useGetResolutionsStatusMutation();
  const [
    createResolution,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, error: errorCreate },
  ] = useCreateResolutionMutation();
  const [
    updateTemplate,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, error: errorUpdate },
  ] = useUpdateResolutionMutation();
  // const [
  //   changeResolutionState,
  //   { isLoading: isLoadingState, isSuccess: isSuccessState, error: errorState },
  // ] = useDeleteResolutionMutation();

  const estatusOptions = React.useMemo(() => {
    return (statusList?.data ?? []).map((st: any) => ({
      label: translatedData.estados[st.estadoNombre] || st.estadoNombre,
      value: st.estadoId,
    }));
  }, [statusList?.data, translatedData.estados]);

  useEffect(() => {
    getResolutionsStatus();
  }, [getResolutionsStatus]);

  useEffect(() => {
    getResolutions();
  }, [getResolutions, isSuccessCreate, isSuccessUpdate, errorCreate, errorUpdate]);

  useEffect(() => {
    setDrawerOpen(false);
    if (errorCreate) toast.error(`Error: ${errorCreate.data?.message}`, toastErrorStyle);

    if (isSuccessCreate) {
      showSuccessToast();
      resetFields();
      setResolucionId(0);
    }
  }, [isSuccessCreate, resetFields, errorCreate, showSuccessToast]);

  useEffect(() => {
    setDrawerOpen(false);
    if (errorUpdate) toast.error(`Error: ${errorUpdate.data?.message}`, toastErrorStyle);

    if (isSuccessUpdate) {
      showSuccessToast();
      resetFields();
      setResolucionId(0);
    }
  }, [isSuccessUpdate, resetFields, errorUpdate, showSuccessToast]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;
      const hasStatusList = statusList?.data && Array.isArray(statusList.data) && statusList.data.length > 0;

      if (!hasData && !hasStatusList) {
        setTranslatedData({ resoluciones: {}, estados: {} });
        return;
      }

      const resolucionesTranslations: { [key: string]: string | null } = {};
      const estadosTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      // Traducir datos de la tabla
      if (hasData) {
        data.data.forEach((record: IResolution) => {
          const resolucionNombre = record.resolucionNombre || record.nombre;
          if (resolucionNombre && !resolucionesTranslations.hasOwnProperty(resolucionNombre)) {
            resolucionesTranslations[resolucionNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, resolucionNombre, locale).then((translated) => {
                resolucionesTranslations[resolucionNombre] = translated;
              })
            );
          }
          const estadoNombre = (record as any).estadoNombre;
          if (estadoNombre && !estadosTranslations.hasOwnProperty(estadoNombre)) {
            estadosTranslations[estadoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, estadoNombre, locale).then((translated) => {
                estadosTranslations[estadoNombre] = translated;
              })
            );
          }
        });
      }

      // Traducir estados del Select
      if (hasStatusList) {
        statusList.data.forEach((st: any) => {
          if (st.estadoNombre && !estadosTranslations.hasOwnProperty(st.estadoNombre)) {
            estadosTranslations[st.estadoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, st.estadoNombre, locale).then((translated) => {
                estadosTranslations[st.estadoNombre] = translated;
              })
            );
          }
        });
      }

      await Promise.all(translationPromises);
      setTranslatedData({
        resoluciones: resolucionesTranslations as { [key: string]: string },
        estados: estadosTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [data?.data, statusList?.data, locale]);

  // useEffect(() => {
  //   if (errorState) {
  //     toast.error(`Error: ${errorState.data?.message}`, toastErrorStyle);
  //   }

  //   if (isSuccessState) {
  //     toast.success("Estado actualizado correctamente.", toastSuccessStyle);
  //   }
  // }, [errorState, isSuccessState]);

  const onClickEditarResolucion = React.useCallback(
    (record: IResolution) => {
      setResolucionId(record.resolucionId);
      setFieldsValue({ nombre: record.resolucionNombre, estadoId: record.estadoId });
      setDrawerOpen(true);
    },
    [setFieldsValue],
  );

  // const cambiarEstadoResolucion = React.useCallback(
  //   (value: number, record: IResolution) => {
  //     changeResolutionState({ nombre: record.resolucionNombre, estadoId: value !== 12 ? 12 : 11 });
  //   },
  //   [changeResolutionState],
  // );

  const submit = () => {
    validateFields(["nombre", "estadoId"])
      .then((values) => {
        const v = {
          ...values,
        };
        if (resolucionId !== 0) {
          updateTemplate({
            ...v,
            id: resolucionId,
          });
        } else {
          createResolution(v);
        }
      })
      .catch(() => {
        showErrorFieldsNeedReviewToast();
      });
  };

  const columns = React.useMemo<ColumnsType<IResolution>>(
    () => [
      {
        title: settingsPage?.resolution_setting_table?.[0]?.label || "Resolución",
        dataIndex: "resolucionNombre",
        render: (_: any, record: IResolution) => {
          const resolucionNombre = record.resolucionNombre || record.nombre || "";
          return resolucionNombre ? (translatedData.resoluciones[resolucionNombre] || resolucionNombre) : "";
        },
      },
      {
        title: settingsPage?.resolution_setting_table?.[1]?.label || "Estado",
        dataIndex: "estadoNombre",
        render: (text: string) => translatedData.estados[text] || text,
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            className="cursor-pointer"
            key={record.resolucionId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: settingsPage?.edit_button || "Editar",
                  icon: <Icon name="" />,
                  onClick: () => onClickEditarResolucion(record),
                },
                // {
                //   key: "2",
                //   label: record.estadoId !== 12 ? "Inactivar" : "Activar",
                //   icon: <Icon name={record.estadoId !== 12 ? "" : ""} />,
                //   onClick: () => cambiarEstadoResolucion(record.estadoId, record),
                // },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarResolucion, settingsPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
              <Skeleton.Input active size="large" style={{ width: 200 }} />
              <Skeleton.Input active size="default" style={{ width: 150 }} />
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </>
        ) : (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between" }}>
              <LargeHeadingStyled>{settingsPage?.resolution__nb || "Resoluciones"}</LargeHeadingStyled>

              <Button
                title=""
                type="primary"
                loading={false}
                onClick={() => {
                  setDrawerOpen(true);
                  resetFields();
                }}
              >
                {settingsPage?.resolution_detail?.new_resolution_button || "Nueva resolución"}
              </Button>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<IResolution>
                loading={isLoading || isLoadingCreate || isLoadingUpdate}
                columns={columns}
                dataSource={data?.data}
                rowKey={(v) => v.resolucionId}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
              <Drawer
                placement="right"
                title={resolucionId !== 0 ? (settingsPage?.resolution_detail?.title || "Edición de resolución") : (settingsPage?.resolution_detail?.new_resolution_button || "Nueva resolución")}
                open={drawerOpen}
                onClose={() => {
                  setResolucionId(0);
                  setDrawerOpen(false);
                  resetFields();
                }}
              >
            {" "}
            <Form
              form={form}
              layout="vertical"
              ref={formRef}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
              initialValues={{
                id: 1,
              }}
            >
              <Form.Item
                name="nombre"
                label={settingsPage?.resolution_detail?.name || "Nombre de la resolución"}
                hasFeedback
                required
                rules={[
                  {
                    required: true,
                    message: "Este campo es requerido.",
                  },
                ]}
              >
                <Input type="text" />
              </Form.Item>
              <Form.Item
                name="estadoId"
                label={settingsPage?.resolution_detail?.status_re || "Estado"}
                hasFeedback
                required
                rules={[
                  {
                    required: true,
                    message: "Este campo es requerido.",
                  },
                ]}
              >
                <Select
                  loading={isLoadingStatus}
                  size="large"
                  options={estatusOptions}
                  placeholder={settingsPage?.resolution_detail?.status_re || "Estado de la resolución"}
                />
              </Form.Item>

              <Button
                onClick={() => submit()}
                type="primary"
                size="large"
                htmlType="submit"
                style={{ alignSelf: "flex-end" }}
                loading={isLoadingCreate || isLoadingUpdate}
              >
                {settingsPage?.resolution_detail?.save_button || "Guardar"}
              </Button>
            </Form>
          </Drawer>
        </Card>
        </>
        )}
      </Content>
    </Flex>
  );
};

export default Resolutions;
